import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { FAQ_ICONS, DEFAULT_FAQ_ICON } from "@/lib/faq-icons";
import { MagnifyingGlass, PencilSimple } from "@phosphor-icons/react";
import type { FaqCategory } from "@/types/faq";
import { FAQ_PLACEMENT_META, FaqPlacementIcons } from "./placements";

/** "Prikazuje se na: Početna, Za firme" — ide u title tooltip oznaka. */
function placementTitle(cat: FaqCategory, t: (key: string) => string): string {
  if (cat.placements.length === 0) return t("faqAdmin.placementNoneHint");
  const names = cat.placements.map((p) => t(FAQ_PLACEMENT_META[p].labelKey)).join(", ");
  return `${t("faqAdmin.placements")}: ${names}`;
}

export function FaqCategoryTabs({
  categories,
  activeCategoryId,
  searchQuery,
  onCategoryChange,
  onSearchChange,
  onEditCategory,
}: {
  categories: FaqCategory[];
  activeCategoryId: string;
  searchQuery: string;
  onCategoryChange: (id: string) => void;
  onSearchChange: (value: string) => void;
  onEditCategory: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto rounded-xl bg-muted/60 p-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {categories.map((cat) => {
            const isActive = cat.id === activeCategoryId;
            const Icon = FAQ_ICONS[cat.icon] ?? FAQ_ICONS[DEFAULT_FAQ_ICON];
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => onCategoryChange(cat.id)}
                className={cn(
                  "flex max-w-[200px] shrink-0 items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-background text-foreground shadow-sm ring-1 ring-border/50"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon
                  className={cn(
                    "size-4 shrink-0",
                    isActive ? "text-primary" : "text-muted-foreground/50",
                  )}
                  weight={isActive ? "fill" : "regular"}
                />
                <span className="truncate">{cat.nameBs}</span>
                <FaqPlacementIcons
                  placements={cat.placements}
                  title={placementTitle(cat, t)}
                  className={isActive ? "text-muted-foreground" : "text-muted-foreground/50"}
                />
                {!cat.isActive && (
                  <span className="size-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
                )}
              </button>
            );
          })}
        </div>
        {activeCategoryId && (
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            className="shrink-0"
            onClick={onEditCategory}
            title={t("faqAdmin.editCategory")}
          >
            <PencilSimple className="size-4" />
          </Button>
        )}
      </div>

      <div className="relative w-full md:w-72">
        <MagnifyingGlass className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={t("faqAdmin.searchPlaceholder")}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10"
          disabled={!activeCategoryId}
        />
      </div>
    </div>
  );
}
